import { StandardEvents } from '@shopify/events';

// Follow the same resolved variant response as Horizon's price/inventory blocks.
class PebbleProductNote extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.section?.addEventListener(StandardEvents.productSelect, this.update);
    this.addEventListener('click', this.click);
    this.tick();
    if (this.dataset.kind === 'countdown') this.timer = setInterval(() => this.tick(), 1000);
  }
  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.update);
    this.removeEventListener('click', this.click);
    clearInterval(this.timer);
  }
  update = (event) => {
    if (!['sale', 'batch'].includes(this.dataset.kind) || event.target.closest('product-card')) return;
    event.promise.then(({ detail }) => {
      const replacement = detail?.html?.querySelector(`pebble-product-note[data-block-id="${this.dataset.blockId}"]`);
      if (replacement) this.innerHTML = replacement.innerHTML;
    }).catch(() => {});
  };
  tick() {
    const row = this.querySelector('[data-end]');
    if (!row) return;
    const remaining = Date.parse(row.dataset.end) - Date.now();
    row.hidden = !Number.isFinite(remaining) || remaining <= 0;
    if (row.hidden) return;
    const seconds = Math.floor(remaining / 1000);
    row.querySelector('[data-clock]').textContent = [Math.floor(seconds / 86400), Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60].map(n => String(n).padStart(2, '0')).join(' : ');
  }
  click = (event) => {
    if (event.target.closest('[data-open-guide]')) this.querySelector('dialog')?.showModal();
    if (event.target.closest('[data-close]')) this.querySelector('dialog')?.close();
  };
}

class PebbleProductVideos extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.addEventListener('click', this.click);
    this.dialog?.addEventListener('close', this.pause);
  }
  disconnectedCallback() {
    this.pause();
    this.removeEventListener('click', this.click);
    this.dialog?.removeEventListener('close', this.pause);
  }
  pause = () => this.querySelectorAll('video').forEach(video => video.pause());
  show(index) {
    this.pause();
    this.index = String(index);
    this.querySelectorAll('[data-video-panel]').forEach(panel => { panel.hidden = panel.dataset.videoPanel !== this.index; });
    this.querySelectorAll('[data-video-index]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.videoIndex === this.index)));
    if (!this.dialog.open) this.dialog.showModal();
    this.querySelector(`[data-video-panel="${this.index}"] video`)?.play().catch(() => {});
  }
  click = (event) => {
    const thumb = event.target.closest('[data-video-index]');
    if (thumb) this.show(thumb.dataset.videoIndex);
    if (event.target.closest('[data-close]')) this.dialog.close();
    const step = event.target.closest('[data-video-step]');
    if (step) {
      const panels = [...this.querySelectorAll('[data-video-panel]')].map(p => p.dataset.videoPanel);
      this.show(panels[(panels.indexOf(this.index) + Number(step.dataset.videoStep) + panels.length) % panels.length]);
    }
  };
}

document.addEventListener('click', event => {
  const link = event.target.closest('[data-pebble-media]');
  if (!link) return;
  const target = document.getElementById(link.hash.slice(1));
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  link.closest('nav').querySelectorAll('a').forEach(a => a.setAttribute('aria-current', String(a === link)));
});
if (!customElements.get('pebble-product-note')) customElements.define('pebble-product-note', PebbleProductNote);
if (!customElements.get('pebble-product-videos')) customElements.define('pebble-product-videos', PebbleProductVideos);

// Horizon's desktop image click closes the dialog. In this PDP it toggles
// fit/enlarged instead; keep the native close button and Escape behavior.
function initializePebbleZoom(root = document) {
  root.querySelectorAll('.pebble-pdp zoom-dialog').forEach(gallery => {
    if (gallery.dataset.pebbleZoomReady) return;
    gallery.dataset.pebbleZoomReady = 'true';
    const dialog = gallery.querySelector('dialog');
    const reset = () => gallery.querySelectorAll('.pebble-zoom-expanded').forEach(item => {
      item.classList.remove('pebble-zoom-expanded');
      item.setAttribute('aria-pressed', 'false');
    });
    dialog.addEventListener('close', reset);
    gallery.querySelectorAll('.product-media-container--image[ref="media[]"]').forEach(item => {
      item.tabIndex = 0;
      item.setAttribute('role', 'button');
      item.setAttribute('aria-label', 'Zoom image');
      item.setAttribute('aria-pressed', 'false');
    });
    const toggle = event => {
      if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const item = event.target.closest('.product-media-container--image[ref="media[]"]');
      if (!item || !dialog.open) return;
      if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const expanded = item.classList.toggle('pebble-zoom-expanded');
      item.setAttribute('aria-pressed', String(expanded));
      if (!expanded) item.scrollIntoView({behavior: 'instant', block: 'start'});
    };
    gallery.addEventListener('click', toggle, true);
    gallery.addEventListener('keydown', toggle, true);
  });
}
initializePebbleZoom();
document.addEventListener('shopify:section:load', () => initializePebbleZoom());
